/**
 * adapters/branding/contrastSpecification — especificação de contraste WCAG AA
 * para theming white-label (RNF 9, PBT-03, Req 20).
 *
 * Rejeita pares de cor que não atinjam o contraste mínimo exigido para texto
 * essencial. Função pura (sem React). A UI de branding (TASK-19) consome esta
 * especificação para sinalizar/recusar combinações inacessíveis.
 *
 * Algoritmo: luminância relativa sRGB (WCAG 2.x) e razão de contraste
 * `(L_claro + 0.05) / (L_escuro + 0.05)`, no intervalo [1, 21].
 */

/** Contraste mínimo WCAG AA para texto normal. */
export const WCAG_AA_NORMAL = 4.5;
/** Contraste mínimo WCAG AA para texto grande (≥ 18pt / 14pt bold). */
export const WCAG_AA_LARGE = 3;

export type ContrastLevel = "AA" | "AA-large";

interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Converte `#RGB` ou `#RRGGBB` em canais 0–255. Lança em formato inválido. */
export function parseHexColor(hex: string): Rgb {
  const cleaned = hex.trim().replace(/^#/, "");
  const expanded =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((c) => c + c)
          .join("")
      : cleaned;
  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
    throw new Error(`Cor hexadecimal inválida: "${hex}"`);
  }
  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
}

function linearizeChannel(value0to255: number): number {
  const c = value0to255 / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function relativeLuminance({ r, g, b }: Rgb): number {
  return (
    0.2126 * linearizeChannel(r) +
    0.7152 * linearizeChannel(g) +
    0.0722 * linearizeChannel(b)
  );
}

/** Razão de contraste WCAG entre duas cores (`#hex`), no intervalo [1, 21]. */
export function contrastRatio(colorA: string, colorB: string): number {
  const la = relativeLuminance(parseHexColor(colorA));
  const lb = relativeLuminance(parseHexColor(colorB));
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** Threshold mínimo para o nível informado. */
export function thresholdFor(level: ContrastLevel): number {
  return level === "AA-large" ? WCAG_AA_LARGE : WCAG_AA_NORMAL;
}

/**
 * Especificação: o par (foreground, background) é acessível se sua razão de
 * contraste atinge o mínimo do nível (AA normal = 4,5:1 por padrão).
 */
export function isAccessible(
  foreground: string,
  background: string,
  level: ContrastLevel = "AA",
): boolean {
  return contrastRatio(foreground, background) >= thresholdFor(level);
}
